import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import PlayArrow from '@mui/icons-material/PlayArrow';
import VolumeOff from '@mui/icons-material/VolumeOff';
import VolumeUp from '@mui/icons-material/VolumeUp';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useReelVideo } from '@/hooks/useReelVideo';

import { ReelProgress } from './ReelProgress';
import { type ViewerVideo } from './types';
import { ViewerCaption } from './ViewerCaption';
import { ViewerIconButton } from './ViewerIconButton';
import { VIEWER_INK_MUTED, VIEWER_SCRIM } from './viewerTokens';

type Props = {
  video: ViewerVideo;
  /** The slide filling the frame. Only this one plays. */
  active: boolean;
  /** False outside the preload window: the element holds no source at all. */
  loaded: boolean;
  muted: boolean;
  onToggleMute: () => void;
  position: string;
  /** Drawn above the caption on the first slide only. */
  hint?: string | undefined;
};

/**
 * One clip, full screen.
 *
 * ## The source is conditional, and that is the point
 *
 * `src` is only attached once the feed says this slide is within a step of the
 * live one. `preload="none"` would not be enough on its own — an element with
 * a source still resolves DNS, opens a connection and fetches metadata — so a
 * forty-clip feed would open forty connections before a visitor had watched
 * anything. Outside the window the element is a poster and nothing else.
 *
 * ## The clip gets a column, not the window
 *
 * A portrait clip fills a column the height of the screen and 9:16 wide,
 * centred — the whole screen on a phone, about 550px on a desktop. A landscape
 * clip takes the full width and is letterboxed rather than cropped, because
 * filling a 9:16 frame with 16:9 footage throws away two thirds of it — and
 * what gets thrown away is the car.
 *
 * ## Tapping pauses
 *
 * The whole frame is the play/pause control, which is what the gesture does in
 * every feed of this shape. It is a real `<button>` wrapping nothing, laid
 * over the video, so it has a name, takes focus and answers the space bar;
 * the caption and the mute control sit above it and stop their own clicks.
 */
export function ReelSlide({ video, active, loaded, muted, onToggleMute, position, hint }: Props) {
  const player = useReelVideo({ active, muted, loaded });
  /** Shot for this feed: it fills its column. A landscape clip is letterboxed. */
  const upright = video.orientation === 'portrait' || video.orientation === 'square';

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        flex: '0 0 100%',
        scrollSnapAlign: 'start',
        // Without this a fast flick sails past three clips and lands wherever
        // momentum ran out, which is how a feed loses somebody's place.
        scrollSnapStop: 'always',
        // Transparent, not its own colour: the bars beside a landscape clip
        // then show the dialog's frosted glass rather than a second flat
        // panel laid on top of it.
        bgcolor: 'transparent',
        overflow: 'hidden',
        // The slide stays full-bleed because snapping needs it to; the stage
        // inside it is what the clip is actually drawn in.
        display: 'grid',
        placeItems: 'center',
      }}
    >
      {/*
        The column the clip lives in.

        On a phone it is the whole screen, which is what a vertical clip was
        shot for. On a desktop it is capped at the width a 9:16 clip needs to
        fill the height — about 550px on a 980px-tall window — and centred,
        which is what Reels, Shorts and TikTok all do on the web. Without the
        cap, `cover` crops a portrait clip to a 1440px-wide letterbox and
        throws away the top and bottom of every frame, which in a handover
        clip is the car and the person. The glass either side is the dialog's
        own, so the result reads as a lit stage rather than a gap.

        A landscape clip takes the full width and is letterboxed instead: it
        has nothing to gain from a narrow column.
      */}
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          width: upright ? { xs: '100%', md: 'min(100%, calc(100svh * 9 / 16))' } : '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          component="video"
          ref={player.videoRef}
          // A source only within the preload window; the poster always.
          {...(loaded ? { src: video.src } : {})}
          {...(video.poster ? { poster: video.poster } : {})}
          preload={loaded ? 'auto' : 'none'}
          playsInline
          loop
          muted={muted}
          aria-label={video.label}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: upright ? 'cover' : 'contain',
            display: 'block',
          }}
        />

        {/* The frame as a control. Transparent, full-bleed, and under the
          overlay so the caption's own link still works. */}
        <Box
          component="button"
          type="button"
          aria-label={player.playing ? `Pause ${video.label}` : `Play ${video.label}`}
          onClick={player.togglePlay}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            border: 0,
            p: 0,
            background: 'none',
            cursor: 'pointer',
            zIndex: 1,
            '&:focus-visible': { outline: '2px solid #FFFFFF', outlineOffset: -4 },
          }}
        />

        {/* Shown whenever the clip is not running — paused by the visitor, or
          refused by the browser, or standing still for reduced motion. The
          states are deliberately not distinguished: the remedy is the same
          button in all three. */}
        {!player.playing && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <Box sx={{ pointerEvents: 'auto' }}>
              <ViewerIconButton
                label={`Play ${video.label}`}
                icon={<PlayArrow sx={{ fontSize: 32 }} />}
                size={56}
                onClick={player.togglePlay}
              />
            </Box>
          </Box>
        )}

        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            pt: 16,
            pb: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
            px: { xs: 4, md: 6 },
            // The caption needs a floor to be read against; a clip's bottom
            // third is whatever the camera happened to see.
            background: VIEWER_SCRIM,
            pointerEvents: 'none',
            '& > *': { pointerEvents: 'auto' },
          }}
        >
          {/* In the column, not floating over it. Positioned absolutely it
            landed on top of the quotation and the button — and it is drawn
            exactly where a caption is longest, so it could not have been
            anywhere else without pushing the overlay around. */}
          {hint && (
            <Stack aria-hidden alignItems="center" sx={{ pb: 3, pointerEvents: 'none' }}>
              <KeyboardArrowUp sx={{ color: VIEWER_INK_MUTED, fontSize: 20 }} />
              <Typography variant="caption" sx={{ color: VIEWER_INK_MUTED, fontWeight: 600 }}>
                {hint}
              </Typography>
            </Stack>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 4,
            }}
          >
            <Box>
              {video.caption && <ViewerCaption caption={video.caption} variant="overlay" />}
              <Box
                component="p"
                className="tabular-nums"
                sx={{ m: 0, mt: 3, fontSize: 12, color: VIEWER_INK_MUTED, fontWeight: 600 }}
              >
                {position}
              </Box>
            </Box>

            <ViewerIconButton
              label={muted ? 'Unmute' : 'Mute'}
              icon={muted ? <VolumeOff /> : <VolumeUp />}
              onClick={onToggleMute}
            />
          </Box>
        </Box>

        <ReelProgress value={player.progress} />
      </Box>
    </Box>
  );
}
